import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  FiChevronDown,
  FiClipboard,
  FiCreditCard,
  FiFileText,
  FiLogOut,
  FiUser,
} from 'react-icons/fi';
import { authService } from '../api/services';
import BrandLogo from './BrandLogo';
import NotificationBell from './NotificationBell';
import { useLanguage } from '../contexts/LanguageContext';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);
  const navbarRef = useRef(null);
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => { setIsMenuOpen(false); setIsAccountMenuOpen(false); }, [location.pathname]);
  useEffect(() => {
    const close = (event) => {
      if (event.key === 'Escape' || (event.type === 'mousedown' && !navbarRef.current?.contains(event.target))) {
        setIsMenuOpen(false); setIsAccountMenuOpen(false);
      }
    };
    document.addEventListener('keydown', close); document.addEventListener('mousedown', close);
    return () => { document.removeEventListener('keydown', close); document.removeEventListener('mousedown', close); };
  }, []);
  const isAuthenticated = authService.isAuthenticated();
  const user = authService.getCurrentUser();
  const roleLabels = {
    admin: 'Admin',
    landlord: 'Chủ trọ',
    saler: 'Saler',
    user: 'User',
  };

  useEffect(() => {
    const theme = localStorage.getItem('theme') === 'dark' ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setIsAccountMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error("Lỗi logout:", error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login');
    }
  };

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
    setIsAccountMenuOpen(false);
  };


  return (
    <nav className="navbar" ref={navbarRef}>
      <div className="container navbar-container">
        <div className="navbar-left">
          <Link to="/rooms" className="navbar-brand" onClick={handleCloseMenu}>
            <BrandLogo />
          </Link>

        </div>

        <button
          type="button"
          className="navbar-toggle"
          aria-label={t('nav.toggleMenu')}
          aria-expanded={isMenuOpen}
          aria-controls="main-navigation"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? '✕' : '☰'}
        </button>

        <ul id="main-navigation" className={`navbar-menu ${isMenuOpen ? 'is-open' : ''}`}>
          <li><Link to="/rooms" className="navbar-link" onClick={handleCloseMenu}>{t('nav.rooms')}</Link></li>

          {isAuthenticated ? (
            <>
              <li><Link to="/dashboard" className="navbar-link" onClick={handleCloseMenu}>{t('nav.dashboard')}</Link></li>
              {['admin', 'landlord'].includes(user?.role) && <li><Link to="/projects" className="navbar-link" onClick={handleCloseMenu}>{language === 'en' ? 'Projects' : 'Dự án'}</Link></li>}
              {user?.role==='saler'&&<li><Link to="/inventory" className="navbar-link" onClick={handleCloseMenu}>Kho dự án</Link></li>}

              {['landlord', 'saler', 'admin'].includes(user?.role) && (
                <>
                  <li><Link to="/my-rooms" className="navbar-link" onClick={handleCloseMenu}>{t('nav.myRooms')}</Link></li>
                  <li><Link to="/create-room" className="navbar-link" onClick={handleCloseMenu}>{t('nav.createRoom')}</Link></li>
                </>
              )}

              <li><Link to="/bookings" className="navbar-link" onClick={handleCloseMenu}>{t('nav.bookings')}</Link></li>

              {user?.role === 'admin' && (
                <>
                  <li><Link to="/admin/rooms" className="navbar-link" onClick={handleCloseMenu}>{language === 'en' ? 'Manage rooms' : 'Quản lý phòng'}</Link></li>
                  <li><Link to="/users" className="navbar-link" onClick={handleCloseMenu}>{t('nav.users')}</Link></li>
                </>
              )}

              <li>
                <div className="navbar-account-wrapper" ref={accountMenuRef}>
                  <button type="button" className="navbar-account" onClick={() => setIsAccountMenuOpen((previous) => !previous)} aria-expanded={isAccountMenuOpen}>
                    <span className="navbar-account-avatar"><FiUser size={18} /></span>
                    <span className="navbar-account-info">
                      <strong>{user?.name || user?.email || t('nav.profile')}</strong>
                      <small>{roleLabels[user?.role] || 'User'}</small>
                    </span>
                    <FiChevronDown className={`navbar-account-chevron ${isAccountMenuOpen ? 'is-open' : ''}`} size={15} />
                  </button>
                  {isAccountMenuOpen && (
                    <div className="navbar-account-menu">
                      <Link to="/profile" className="navbar-account-menu-item" onClick={handleCloseMenu}><FiUser size={16} /> Hồ sơ</Link>
                      <Link to="/wallet/top-up" className="navbar-account-menu-item" onClick={handleCloseMenu}><FiCreditCard size={16} /> Nạp tiền</Link>
                      <Link to="/create-deposit" className="navbar-account-menu-item" onClick={handleCloseMenu}><FiFileText size={16} /> Tạo phiếu cọc</Link>
                      <Link to="/bookings" className="navbar-account-menu-item" onClick={handleCloseMenu}><FiClipboard size={16} /> Quản lý phiếu cọc</Link>
                      <button type="button" className="navbar-account-menu-item navbar-account-menu-danger" onClick={handleLogout}><FiLogOut size={16} /> Đăng xuất</button>
                    </div>
                  )}
                </div>
              </li>
            </>
          ) : (
            <>
              <li><Link to="/login" className="navbar-button secondary" onClick={handleCloseMenu}>{t('nav.login')}</Link></li>
              <li><Link to="/register" className="navbar-button" onClick={handleCloseMenu}>{t('nav.register')}</Link></li>
            </>
          )}
        </ul>
        {isAuthenticated&&<NotificationBell role={user?.role} key={user?.id}/>}
      </div>
    </nav>
  );
};

export default Navbar;
